import React, { useState, useRef } from 'react'

const API = process.env.REACT_APP_API_URL || 'https://sunshoos-v2.onrender.com'
const fmt = n => `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const BLANK_EMP = {
  name: '', ni_number: '', tax_code: '1257L', employee_ref: '',
  gross_pay: '', pension_employee: '', pension_employer: '',
  ytd_gross: '', ytd_tax: '', ytd_nic_employee: '',
  payment_method: 'BACS', payment_date: '',
}

const TAX_CODES = ['1257L', '1100L', 'BR', 'D0', 'D1', '0T', 'NT', 'K code']
const PAYMENT_METHODS = ['BACS', 'Cheque', 'Cash']

export default function RTIPayroll() {
  const [tab, setTab] = useState('manual')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState(null)
  const [saved, setSaved] = useState(false)
  const [activeEmp, setActiveEmp] = useState(0)
  const fileRef = useRef()

  // Employer details
  const [employer, setEmployer] = useState({
    name: '', paye_ref: '', accounts_office_ref: '',
    tax_period: '', payment_date: '', period_type: 'Monthly',
  })
  const setE = (k, v) => setEmployer(e => ({ ...e, [k]: v }))

  // Employees list
  const [employees, setEmployees] = useState([{ ...BLANK_EMP }])
  const setEmp = (i, k, v) => setEmployees(emps => emps.map((e, idx) => idx === i ? { ...e, [k]: v } : e))
  const addEmp = () => { setEmployees(e => [...e, { ...BLANK_EMP }]); setActiveEmp(employees.length) }
  const removeEmp = i => { setEmployees(e => e.filter((_, idx) => idx !== i)); setActiveEmp(0) }

  // CSV import
  const [csvRaw, setCsvRaw] = useState('')
  async function handleFileChange(e) {
    const f = e.target.files[0]; if (!f) return
    const text = await f.text(); setCsvRaw(text)
    // Parse CSV into employees
    const lines = text.trim().split('\n').filter(l => l.trim()).slice(1)
    const parsed = lines.map(line => {
      const cols = line.split(',').map(c => c.trim().replace(/^"|"$/g, ''))
      return {
        name: cols[0] || '', ni_number: cols[1] || '', tax_code: cols[2] || '1257L',
        employee_ref: cols[3] || '', gross_pay: cols[4] || '',
        pension_employee: cols[5] || '0', pension_employer: cols[6] || '0',
        ytd_gross: cols[7] || '0', ytd_tax: cols[8] || '0', ytd_nic_employee: cols[9] || '0',
        payment_method: cols[10] || 'BACS', payment_date: cols[11] || '',
      }
    }).filter(e => e.name)
    if (parsed.length > 0) { setEmployees(parsed); setTab('manual') }
  }

  async function handleSubmit() {
    setLoading(true); setError(''); setResult(null); setSaved(false)
    try {
      const res = await fetch(`${API}/api/payroll/compute`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employer, employees: employees.map(e => ({ ...e, gross_pay: parseFloat(e.gross_pay) || 0, pension_employee: parseFloat(e.pension_employee) || 0, pension_employer: parseFloat(e.pension_employer) || 0, ytd_gross: parseFloat(e.ytd_gross) || 0, ytd_tax: parseFloat(e.ytd_tax) || 0, ytd_nic_employee: parseFloat(e.ytd_nic_employee) || 0 })) })
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.detail || 'Server error')
      setResult(data)
    } catch (e) { setError(e.message) }
    setLoading(false)
  }

  async function handleExport(type) {
    try {
      const res = await fetch(`${API}/api/payroll/export-${type}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(result)
      })
      if (!res.ok) throw new Error(`${type} export failed`)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `RTI_Payroll_${(result.employer?.name || 'Employer').replace(/ /g, '_')}_${(result.employer?.tax_period || '').replace(/ /g, '_')}.${type === 'excel' ? 'xlsx' : 'pdf'}`
      a.click(); URL.revokeObjectURL(url)
    } catch (e) { setError(`Export error: ${e.message}`) }
  }

  async function handleSave() {
    try {
      const res = await fetch(`${API}/api/payroll/save`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(result)
      })
      if (!res.ok) throw new Error('Save failed')
      setSaved(true)
    } catch (e) { setError('Save error: ' + e.message) }
  }

  const s = {
    page: { padding: '24px', maxWidth: 1020, margin: '0 auto', fontFamily: 'Calibri, sans-serif' },
    card: { background: '#0f1923', border: '1px solid rgba(201,151,58,0.15)', borderRadius: 10, padding: 22, marginBottom: 18 },
    row: { display: 'flex', gap: 14, marginBottom: 14, flexWrap: 'wrap' },
    label: { fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 5, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 },
    input: { background: '#0B1929', border: '1px solid rgba(201,151,58,0.2)', borderRadius: 6, color: '#fff', padding: '8px 12px', fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box' },
    tabBar: { display: 'flex', gap: 2, marginBottom: 18, background: '#0B1929', borderRadius: 8, padding: 4 },
    tab: a => ({ flex: 1, padding: '8px 0', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: a ? '#C9973A' : 'transparent', color: a ? '#0B1929' : 'rgba(255,255,255,0.4)', transition: 'all .2s' }),
    btn: (v = 'primary') => ({ padding: '10px 20px', borderRadius: 7, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13, background: v === 'primary' ? '#C9973A' : v === 'green' ? '#1a4a2e' : v === 'blue' ? '#0f2a4a' : v === 'red' ? '#4a1a1a' : '#1a2a4a', color: v === 'primary' ? '#0B1929' : v === 'green' ? '#4ade80' : v === 'blue' ? '#93c5fd' : v === 'red' ? '#f87171' : '#C9973A' }),
    sectionTitle: { fontSize: 11, fontWeight: 700, color: 'rgba(201,151,58,0.7)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 },
    empTab: (a) => ({ padding: '7px 14px', borderRadius: 6, border: `1px solid ${a ? 'rgba(201,151,58,0.5)' : 'rgba(255,255,255,0.08)'}`, cursor: 'pointer', fontSize: 12, fontWeight: a ? 700 : 400, background: a ? 'rgba(201,151,58,0.1)' : 'transparent', color: a ? '#C9973A' : 'rgba(255,255,255,0.4)', marginRight: 6, marginBottom: 6 }),
    statBox: (color) => ({ flex: 1, minWidth: 140, padding: '14px 16px', borderRadius: 8, background: color === 'gold' ? 'rgba(201,151,58,0.1)' : color === 'green' ? 'rgba(74,222,128,0.08)' : color === 'red' ? 'rgba(239,68,68,0.08)' : 'rgba(255,255,255,0.04)', border: `1px solid ${color === 'gold' ? 'rgba(201,151,58,0.25)' : color === 'green' ? 'rgba(74,222,128,0.2)' : color === 'red' ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.08)'}` }),
  }

  const fps = result?.fps_summary || {}
  const checklist = result?.checklist || []
  const payslips = result?.payslips || []

  return (
    <div style={s.page}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#fff', margin: 0 }}>RTI Payroll</h1>
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', margin: '4px 0 0' }}>
          PAYE &amp; NIC · FPS Summary · Payslips · RTI Checklist · 2024/25 tax year
        </p>
      </div>

      {/* Employer Details */}
      <div style={s.card}>
        <div style={s.sectionTitle}>Employer Details</div>
        <div style={s.row}>
          <div style={{ flex: 2, minWidth: 180 }}>
            <label style={s.label}>Employer / Company Name</label>
            <input style={s.input} value={employer.name} onChange={e => setE('name', e.target.value)} placeholder="e.g. Acme Trading Ltd" />
          </div>
          <div style={{ flex: 1, minWidth: 140 }}>
            <label style={s.label}>PAYE Reference</label>
            <input style={s.input} value={employer.paye_ref} onChange={e => setE('paye_ref', e.target.value)} placeholder="e.g. 120/AB12345" />
          </div>
          <div style={{ flex: 1, minWidth: 160 }}>
            <label style={s.label}>Accounts Office Ref</label>
            <input style={s.input} value={employer.accounts_office_ref} onChange={e => setE('accounts_office_ref', e.target.value)} placeholder="e.g. 120PA00012345" />
          </div>
          <div style={{ flex: 1, minWidth: 130 }}>
            <label style={s.label}>Tax Period</label>
            <input style={s.input} value={employer.tax_period} onChange={e => setE('tax_period', e.target.value)} placeholder="e.g. Month 1 (Apr)" />
          </div>
          <div style={{ flex: 1, minWidth: 130 }}>
            <label style={s.label}>Period Type</label>
            <select style={s.input} value={employer.period_type} onChange={e => setE('period_type', e.target.value)}>
              <option>Monthly</option><option>Weekly</option><option>Fortnightly</option>
            </select>
          </div>
          <div style={{ flex: 1, minWidth: 150 }}>
            <label style={s.label}>Payment Date</label>
            <input style={s.input} type="date" value={employer.payment_date} onChange={e => setE('payment_date', e.target.value)} />
          </div>
        </div>
      </div>

      {/* Input tabs */}
      <div style={s.tabBar}>
        <button style={s.tab(tab === 'manual')} onClick={() => setTab('manual')}>👤 Manual Employee Entry</button>
        <button style={s.tab(tab === 'csv')} onClick={() => setTab('csv')}>📄 CSV Import</button>
      </div>

      {/* CSV Import */}
      {tab === 'csv' && (
        <div style={s.card}>
          <div style={s.sectionTitle}>Import Employees via CSV</div>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 14 }}>
            CSV columns (in order): <code style={{ color: '#C9973A' }}>name, ni_number, tax_code, employee_ref, gross_pay, pension_employee, pension_employer, ytd_gross, ytd_tax, ytd_nic_employee, payment_method, payment_date</code>
          </p>
          <div style={{ border: '2px dashed rgba(201,151,58,0.25)', borderRadius: 8, padding: '28px 20px', textAlign: 'center', cursor: 'pointer', marginBottom: 14 }}
            onClick={() => fileRef.current.click()}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>📂</div>
            <div style={{ fontSize: 13, color: csvRaw ? '#C9973A' : 'rgba(255,255,255,0.35)' }}>
              {csvRaw ? `✓ ${employees.length} employee(s) loaded — switching to Manual Entry` : 'Click to upload CSV'}
            </div>
            <input ref={fileRef} type="file" accept=".csv,.txt" style={{ display: 'none' }} onChange={handleFileChange} />
          </div>
        </div>
      )}

      {/* Manual Entry */}
      {tab === 'manual' && (
        <div style={s.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div style={s.sectionTitle}>Employees ({employees.length})</div>
            <button style={s.btn('primary')} onClick={addEmp}>+ Add Employee</button>
          </div>

          {/* Employee tabs */}
          <div style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap' }}>
            {employees.map((emp, i) => (
              <button key={i} style={s.empTab(activeEmp === i)} onClick={() => setActiveEmp(i)}>
                {emp.name || `Employee ${i + 1}`}
              </button>
            ))}
          </div>

          {/* Active employee form */}
          {employees[activeEmp] && (() => {
            const emp = employees[activeEmp]
            const i = activeEmp
            return (
              <div>
                <div style={s.row}>
                  <div style={{ flex: 2, minWidth: 180 }}>
                    <label style={s.label}>Full Name</label>
                    <input style={s.input} value={emp.name} onChange={e => setEmp(i, 'name', e.target.value)} placeholder="e.g. Jane Smith" />
                  </div>
                  <div style={{ flex: 1, minWidth: 140 }}>
                    <label style={s.label}>NI Number</label>
                    <input style={s.input} value={emp.ni_number} onChange={e => setEmp(i, 'ni_number', e.target.value)} placeholder="e.g. AB123456C" />
                  </div>
                  <div style={{ flex: 1, minWidth: 120 }}>
                    <label style={s.label}>Tax Code</label>
                    <select style={s.input} value={emp.tax_code} onChange={e => setEmp(i, 'tax_code', e.target.value)}>
                      {TAX_CODES.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div style={{ flex: 1, minWidth: 130 }}>
                    <label style={s.label}>Employee Ref</label>
                    <input style={s.input} value={emp.employee_ref} onChange={e => setEmp(i, 'employee_ref', e.target.value)} placeholder="e.g. EMP001" />
                  </div>
                </div>

                <div style={s.row}>
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <label style={s.label}>Gross Pay This Period (£)</label>
                    <input style={s.input} type="number" value={emp.gross_pay} onChange={e => setEmp(i, 'gross_pay', e.target.value)} placeholder="0.00" />
                  </div>
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <label style={s.label}>Employee Pension (£)</label>
                    <input style={s.input} type="number" value={emp.pension_employee} onChange={e => setEmp(i, 'pension_employee', e.target.value)} placeholder="0.00" />
                  </div>
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <label style={s.label}>Employer Pension (£)</label>
                    <input style={s.input} type="number" value={emp.pension_employer} onChange={e => setEmp(i, 'pension_employer', e.target.value)} placeholder="0.00" />
                  </div>
                  <div style={{ flex: 1, minWidth: 150 }}>
                    <label style={s.label}>Payment Method</label>
                    <select style={s.input} value={emp.payment_method} onChange={e => setEmp(i, 'payment_method', e.target.value)}>
                      {PAYMENT_METHODS.map(m => <option key={m}>{m}</option>)}
                    </select>
                  </div>
                </div>

                <div style={{ ...s.sectionTitle, marginTop: 8 }}>Year to Date (before this period)</div>
                <div style={s.row}>
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <label style={s.label}>YTD Gross (£)</label>
                    <input style={s.input} type="number" value={emp.ytd_gross} onChange={e => setEmp(i, 'ytd_gross', e.target.value)} placeholder="0.00" />
                  </div>
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <label style={s.label}>YTD Tax Paid (£)</label>
                    <input style={s.input} type="number" value={emp.ytd_tax} onChange={e => setEmp(i, 'ytd_tax', e.target.value)} placeholder="0.00" />
                  </div>
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <label style={s.label}>YTD Employee NIC (£)</label>
                    <input style={s.input} type="number" value={emp.ytd_nic_employee} onChange={e => setEmp(i, 'ytd_nic_employee', e.target.value)} placeholder="0.00" />
                  </div>
                </div>

                {employees.length > 1 && (
                  <button style={{ ...s.btn('red'), marginTop: 8, fontSize: 12, padding: '7px 14px' }} onClick={() => removeEmp(i)}>
                    Remove {emp.name || 'this employee'}
                  </button>
                )}
              </div>
            )
          })()}
        </div>
      )}

      <div style={{ marginBottom: 20 }}>
        <button style={s.btn('primary')} onClick={handleSubmit} disabled={loading}>
          {loading ? '⏳ Computing payroll…' : `▶ Run Payroll (${employees.length} employee${employees.length > 1 ? 's' : ''})`}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#f87171', fontSize: 13 }}>⚠ {error}</div>
      )}

      {/* Results */}
      {result && (
        <>
          {/* Action bar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>{result.employer?.name} — {result.employer?.tax_period}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', marginTop: 3 }}>PAYE Ref: {result.employer?.paye_ref || '—'} · {result.payslips?.length} employee(s) · {result.employer?.period_type}</div>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button style={s.btn('green')} onClick={() => handleExport('excel')}>⬇ Excel</button>
              <button style={s.btn('blue')} onClick={() => handleExport('pdf')}>⬇ PDF Payslips</button>
              <button style={s.btn('save')} onClick={handleSave} disabled={saved}>{saved ? '✓ Saved' : '💾 Save'}</button>
            </div>
          </div>

          {/* FPS Summary stats */}
          <div style={s.card}>
            <div style={s.sectionTitle}>FPS Summary — Full Payment Submission</div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
              {[
                { label: 'Total Gross Pay', val: fps.total_gross, color: 'gold' },
                { label: 'Total PAYE Tax', val: fps.total_paye, color: 'red' },
                { label: 'Total Employee NIC', val: fps.total_nic_employee, color: 'red' },
                { label: 'Total Employer NIC', val: fps.total_nic_employer, color: '' },
                { label: 'Total Pensions', val: fps.total_pension, color: '' },
                { label: 'Total Net Pay', val: fps.total_net, color: 'green' },
              ].map(({ label, val, color }) => (
                <div key={label} style={s.statBox(color)}>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{label}</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: color === 'gold' ? '#C9973A' : color === 'green' ? '#4ade80' : color === 'red' ? '#f87171' : '#fff' }}>{fmt(val)}</div>
                </div>
              ))}
            </div>
            <div style={{ padding: '10px 14px', borderRadius: 7, background: 'rgba(201,151,58,0.07)', border: '1px solid rgba(201,151,58,0.15)', fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>
              <span style={{ color: '#C9973A', fontWeight: 700 }}>PAYE/NIC Due to HMRC: </span>
              <span style={{ color: '#fff', fontWeight: 800, fontSize: 15 }}>{fmt(fps.total_due_hmrc)}</span>
              <span style={{ color: 'rgba(255,255,255,0.4)', marginLeft: 12, fontSize: 11 }}>(PAYE + Employee NIC + Employer NIC)</span>
            </div>
          </div>

          {/* Payslips */}
          <div style={s.card}>
            <div style={s.sectionTitle}>Payslips — {payslips.length} Employee(s)</div>
            {payslips.map((p, i) => (
              <div key={i} style={{ marginBottom: 16, padding: 16, borderRadius: 8, background: '#0B1929', border: '1px solid rgba(255,255,255,0.07)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{p.name}</div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>
                      NI: {p.ni_number || '—'} · Tax Code: {p.tax_code} · Ref: {p.employee_ref || '—'}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>Net Pay</div>
                    <div style={{ fontSize: 20, fontWeight: 800, color: '#4ade80' }}>{fmt(p.net_pay)}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {[
                    ['Gross Pay', p.gross_pay, ''],
                    ['PAYE Tax', p.paye_tax, 'red'],
                    ['Employee NIC', p.nic_employee, 'red'],
                    ['Employer NIC', p.nic_employer, ''],
                    ['Pension (Emp)', p.pension_employee, ''],
                    ['Pension (Er)', p.pension_employer, ''],
                  ].map(([lbl, val, color]) => (
                    <div key={lbl} style={{ padding: '7px 12px', borderRadius: 6, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', minWidth: 110 }}>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginBottom: 3 }}>{lbl}</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: color === 'red' ? '#f87171' : '#fff' }}>{fmt(val)}</div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 10, display: 'flex', gap: 16, fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
                  <span>YTD Gross: <span style={{ color: '#fff' }}>{fmt(p.ytd_gross_updated)}</span></span>
                  <span>YTD Tax: <span style={{ color: '#fff' }}>{fmt(p.ytd_tax_updated)}</span></span>
                  <span>YTD NIC: <span style={{ color: '#fff' }}>{fmt(p.ytd_nic_updated)}</span></span>
                  <span>Payment: <span style={{ color: '#C9973A' }}>{p.payment_method}</span></span>
                </div>
              </div>
            ))}
          </div>

          {/* RTI Checklist */}
          <div style={s.card}>
            <div style={s.sectionTitle}>RTI Submission Checklist</div>
            {checklist.map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '9px 12px', borderRadius: 6, marginBottom: 6, background: item.status === 'ok' ? 'rgba(74,222,128,0.05)' : item.status === 'warning' ? 'rgba(251,191,36,0.05)' : 'rgba(239,68,68,0.05)', border: `1px solid ${item.status === 'ok' ? 'rgba(74,222,128,0.15)' : item.status === 'warning' ? 'rgba(251,191,36,0.15)' : 'rgba(239,68,68,0.15)'}` }}>
                <span style={{ fontSize: 14, flexShrink: 0 }}>{item.status === 'ok' ? '✅' : item.status === 'warning' ? '⚠️' : '❌'}</span>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: item.status === 'ok' ? '#4ade80' : item.status === 'warning' ? '#FBBF24' : '#f87171' }}>{item.title}</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>{item.detail}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
